import React, { useState } from 'react';
import Button from '../Common/Button';
import { formatUSDT, bpsToPercent, parseUSDT } from '../../utils/format';
import { useWeb3 } from '../../context/Web3Context';
import { notify, removeNotify } from '../../utils/notify';
import { parseContractError } from '../../utils/errors';
import { ethers } from 'ethers';
import { MIN_CLAIM_USDT } from '../../config/contract';

const MIN_CLAIM_BN = ethers.utils.parseUnits(MIN_CLAIM_USDT.toString(), 18);

const PendingRewards = ({ pendingRewards, pendingROI, claimFee, dailyLimit, remaining, onChange }) => {
  const { contract, onCorrectNetwork, switchToCorrectNetwork } = useWeb3();
  const [busy, setBusy] = useState(false);
  const [amountInput, setAmountInput] = useState('');

  const totalAvailable = ethers.BigNumber.from(pendingRewards || '0')
    .add(ethers.BigNumber.from(pendingROI || '0'));

  const limitEnabled = dailyLimit && dailyLimit !== '0';
  const maxClaimable = limitEnabled
    ? (totalAvailable.lt(ethers.BigNumber.from(remaining || '0'))
        ? totalAvailable
        : ethers.BigNumber.from(remaining || '0'))
    : totalAvailable;

  let claimTarget;
  try {
    claimTarget = amountInput ? parseUSDT(amountInput) : maxClaimable;
  } catch (e) {
    claimTarget = ethers.BigNumber.from(0);
  }
  if (claimTarget.gt(maxClaimable)) claimTarget = maxClaimable;

  const feeBN = claimTarget.mul(ethers.BigNumber.from(claimFee || '1000')).div(10000);
  const payout = claimTarget.sub(feeBN);

  const fillMax = () => setAmountInput(ethers.utils.formatUnits(maxClaimable, 18));

  const claim = async () => {
    if (!contract) return;
    if (!onCorrectNetwork) {
      notify.warning('Switching to correct network...');
      const ok = await switchToCorrectNetwork();
      if (!ok) return;
      await new Promise((r) => setTimeout(r, 600));
    }
    if (totalAvailable.lte(0)) {
      notify.warning('No rewards available to claim');
      return;
    }

    let amountArg = ethers.BigNumber.from(0);
    const effectiveAmount = amountInput ? parseUSDT(amountInput) : maxClaimable;

    if (effectiveAmount.lt(MIN_CLAIM_BN)) {
      notify.error(`Minimum claim is ${MIN_CLAIM_USDT} USDT`);
      return;
    }

    if (amountInput) {
      if (effectiveAmount.lte(0)) {
        notify.error('Enter a valid amount');
        return;
      }
      if (effectiveAmount.gt(maxClaimable)) {
        notify.error(`Amount exceeds available (${formatUSDT(maxClaimable)} USDT)`);
        return;
      }
      amountArg = effectiveAmount;
    }

    setBusy(true);
    let lid = null;
    try {
      lid = notify.loading('Claiming rewards...');
      const tx = await contract.claimRewards(amountArg);
      await tx.wait();
      removeNotify(lid); lid = null;
      notify.success('Rewards claimed successfully');
      setAmountInput('');
      onChange && onChange();
    } catch (err) {
      if (lid) removeNotify(lid);
      notify.error(parseContractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pending-rewards-box">
      <div className="pending-label">Available to Claim</div>
      <div className="pending-amount">{formatUSDT(totalAvailable)}</div>
      <div className="pending-currency">USDT</div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 32,
        marginBottom: 20,
        fontSize: 12,
        color: 'var(--text-muted)',
        letterSpacing: '0.05em',
        flexWrap: 'wrap'
      }}>
        <div>
          <div style={{ textTransform: 'uppercase', marginBottom: 4 }}>Claiming</div>
          <div style={{ fontFamily: 'JetBrains Mono', fontSize: 14, color: 'var(--text-primary)' }}>
            {formatUSDT(claimTarget)}
          </div>
        </div>
        <div>
          <div style={{ textTransform: 'uppercase', marginBottom: 4 }}>Fee ({bpsToPercent(claimFee || 0)}%)</div>
          <div className="text-pink" style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
            -{formatUSDT(feeBN)}
          </div>
        </div>
        <div>
          <div style={{ textTransform: 'uppercase', marginBottom: 4 }}>You Receive</div>
          <div className="text-green" style={{ fontFamily: 'JetBrains Mono', fontSize: 14 }}>
            {formatUSDT(payout)}
          </div>
        </div>
      </div>

      <div style={{
        maxWidth: 380,
        margin: '0 auto 16px',
        display: 'flex',
        gap: 8,
        background: 'var(--bg-input)',
        border: '1px solid var(--border-secondary)',
        borderRadius: 'var(--radius-md)',
        padding: 4
      }}>
        <input
          type="text"
          className="claim-amount-input"
          placeholder={`Max ${formatUSDT(maxClaimable)} USDT`}
          value={amountInput}
          onChange={(e) => setAmountInput(e.target.value.replace(/[^0-9.]/g, ''))}
          disabled={busy || maxClaimable.lte(0)}
          style={{ textAlign: 'center', fontSize: 16 }}
        />
        <button
          type="button"
          className="claim-max-btn"
          onClick={fillMax}
          disabled={maxClaimable.lte(0)}
        >Max</button>
      </div>

      <Button
        variant="success"
        size="lg"
        onClick={claim}
        loading={busy}
        disabled={totalAvailable.lte(0) || maxClaimable.lt(MIN_CLAIM_BN)}
      >
        {amountInput ? 'Claim Amount' : 'Claim All Available'}
      </Button>

      <div style={{
        marginTop: 12,
        fontSize: 11,
        color: 'var(--text-muted)',
        letterSpacing: '0.05em'
      }}>
        Min claim {MIN_CLAIM_USDT} USDT · empty input claims the max (subject to 24h cap)
      </div>
    </div>
  );
};

export default PendingRewards;
